import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from './authService';

export const vendorExternalLeadApi = {
  listLinks: () => api.get('/vendor/lead-links').then((r) => r.data),
  createLink: (payload) => api.post('/vendor/lead-links', payload).then((r) => r.data),
  updateLink: (id, payload) => api.put(`/vendor/lead-links/${id}`, payload).then((r) => r.data),
  deleteLink: (id) => api.delete(`/vendor/lead-links/${id}`).then((r) => r.data),
  listLeads: (params = {}) =>
    api.get('/vendor/external-leads', { params }).then((r) => r.data),
  board: (params = {}) =>
    api.get('/vendor/external-leads/board', { params }).then((r) => r.data),
  calendar: (params = {}) =>
    api.get('/vendor/external-leads/calendar', { params }).then((r) => r.data),
  catalog: () => api.get('/vendor/external-leads/catalog').then((r) => r.data),
  getLead: (id) => api.get(`/vendor/external-leads/${id}`).then((r) => r.data),
  updateLead: (id, payload) =>
    api.put(`/vendor/external-leads/${id}`, payload).then((r) => r.data),
  updateStatus: (id, payload) =>
    api.patch(`/vendor/external-leads/${id}/status`, payload).then((r) => r.data),
  updateChecklist: (id, payload) =>
    api.patch(`/vendor/external-leads/${id}/checklist`, payload).then((r) => r.data),
  updateDeal: (id, payload) =>
    api.patch(`/vendor/external-leads/${id}/deal`, payload).then((r) => r.data),
  convertLead: (id, payload = {}) =>
    api
      .post(`/vendor/external-leads/${id}/convert-to-private-booking`, payload)
      .then((r) => r.data),
  deleteLead: (id) => api.delete(`/vendor/external-leads/${id}`).then((r) => r.data),
  sendMeetingEmail: (id, payload = {}) =>
    api.post(`/vendor/external-leads/${id}/send-meeting-email`, payload).then((r) => r.data),
  reminders: (params = {}) =>
    api.get('/vendor/external-leads/reminders', { params }).then((r) => r.data),
  completeReminder: (reminderId, payload) =>
    api
      .post(`/vendor/external-leads/reminders/${reminderId}/complete`, payload)
      .then((r) => r.data),
};

export const publicLeadLinkApi = {
  get: (token) => api.get(`/public/lead-links/${token}`).then((r) => r.data),
  submit: (token, payload) =>
    api.post(`/public/lead-links/${token}/submit`, payload).then((r) => r.data),
};

const invalidateLeadQueries = (qc) => {
  qc.invalidateQueries({ queryKey: ['vendorExternalLeads'] });
  qc.invalidateQueries({ queryKey: ['vendorExternalLeadBoard'] });
  qc.invalidateQueries({ queryKey: ['vendorExternalLeadCalendar'] });
  qc.invalidateQueries({ queryKey: ['vendorExternalLead'] });
  qc.invalidateQueries({ queryKey: ['vendorExternalLeadReminders'] });
  qc.invalidateQueries({ queryKey: ['pendingExternalLeadCount'] });
  qc.invalidateQueries({ queryKey: ['vendorBookingStats'] });
};

export const getExternalLeadCalendarLeads = async (params = {}) =>
  vendorExternalLeadApi.calendar(params);

/** Pending external lead total for sidebar badges */
export const getPendingExternalLeadCount = async () => {
  const res = await vendorExternalLeadApi.listLeads({ status: 'pending', per_page: 1 });
  const fromCounts = res?.counts?.pending;
  if (fromCounts != null) return Number(fromCounts) || 0;
  return Number(res?.data?.total ?? 0) || 0;
};

export const usePendingExternalLeadCount = (options = {}) =>
  useQuery({
    queryKey: ['pendingExternalLeadCount'],
    queryFn: getPendingExternalLeadCount,
    staleTime: 30_000,
    ...options,
  });

export const useExternalLeadCalendar = (params = {}, options = {}) =>
  useQuery({
    queryKey: ['vendorExternalLeadCalendar', params],
    queryFn: () => getExternalLeadCalendarLeads(params),
    staleTime: 60_000,
    ...options,
  });

export const useVendorExternalLeadReminders = (filter = 'due_today', options = {}) =>
  useQuery({
    queryKey: ['vendorExternalLeadReminders', filter],
    queryFn: () => vendorExternalLeadApi.reminders({ filter }),
    staleTime: 8_000,
    refetchInterval: 20_000,
    refetchIntervalInBackground: false,
    ...options,
  });

export const useCompleteExternalLeadReminder = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ reminderId, ...payload }) =>
      vendorExternalLeadApi.completeReminder(reminderId, payload),
    onSuccess: () => invalidateLeadQueries(qc),
  });
};

export const useVendorLeadLinks = (options = {}) =>
  useQuery({
    queryKey: ['vendorLeadLinks'],
    queryFn: vendorExternalLeadApi.listLinks,
    staleTime: 30_000,
    ...options,
  });

export const useVendorExternalLeads = (params = {}, options = {}) =>
  useQuery({
    queryKey: ['vendorExternalLeads', params],
    queryFn: () => vendorExternalLeadApi.listLeads(params),
    staleTime: 8_000,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    ...options,
  });

export const useVendorExternalLeadBoard = (params = {}, options = {}) =>
  useQuery({
    queryKey: ['vendorExternalLeadBoard', params],
    queryFn: () => vendorExternalLeadApi.board(params),
    staleTime: 8_000,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    ...options,
  });

export const useCreateLeadLink = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: vendorExternalLeadApi.createLink,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendorLeadLinks'] }),
  });
};

export const useUpdateLeadLink = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) => vendorExternalLeadApi.updateLink(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendorLeadLinks'] }),
  });
};

export const useDeleteLeadLink = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: vendorExternalLeadApi.deleteLink,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendorLeadLinks'] }),
  });
};

export const useUpdateExternalLead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) => vendorExternalLeadApi.updateLead(id, payload),
    onSuccess: () => invalidateLeadQueries(qc),
  });
};

export const useUpdateExternalLeadStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) => vendorExternalLeadApi.updateStatus(id, payload),
    onSuccess: () => invalidateLeadQueries(qc),
  });
};

export const useUpdateExternalLeadChecklist = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) => vendorExternalLeadApi.updateChecklist(id, payload),
    onSuccess: () => invalidateLeadQueries(qc),
  });
};

export const useUpdateExternalLeadDeal = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) => vendorExternalLeadApi.updateDeal(id, payload),
    onSuccess: () => invalidateLeadQueries(qc),
  });
};

export const useConvertExternalLead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) => vendorExternalLeadApi.convertLead(id, payload),
    onSuccess: () => {
      invalidateLeadQueries(qc);
      qc.invalidateQueries({ queryKey: ['vendorBookings'] });
    },
  });
};

export const useDeleteExternalLead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => vendorExternalLeadApi.deleteLead(id),
    onSuccess: () => invalidateLeadQueries(qc),
  });
};

export const useSendExternalLeadMeetingEmail = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) => vendorExternalLeadApi.sendMeetingEmail(id, payload),
    onSuccess: () => invalidateLeadQueries(qc),
  });
};
